import React, { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { DRIVERS, LoadFormData, TERMINALS } from '../../config/types';
import { useAppDispatch, useAppSelector } from '../../store';
import { uploadLoad } from '../../store/slices/dispatchSlice';
import {
  describeStops,
  DispatchDetails,
  readDispatchFile,
  SplitInfo,
  splitLabel
} from '../../services/dispatchImport';
import { BRAND } from '../../theme';

type LoadFields = Omit<LoadFormData, 'pdfFile' | 'pdfFileName'>;

interface ReviewRow {
  key: string;
  file: File;
  /** Set when the PDF couldn't be read as a dispatch */
  error?: string;
  load?: LoadFields;
  details?: DispatchDetails;
  /** Set when this dispatch is one drop of a split load */
  split?: SplitInfo | null;
  /** Fields the reader wasn't sure about */
  reviewReasons: string[];
  include: boolean;
  /** Saving state */
  saving?: boolean;
  saved?: boolean;
  saveError?: string;
}

// The load board's own required fields (see LoadBoard handleSubmit).
const REQUIRED: Array<keyof LoadFields> = [
  'broker',
  'destination',
  'loadNumber',
  'deliveryDate',
  'gallons',
  'terminal',
  'driver'
];

const REASON_FIELD: Record<string, keyof LoadFields> = {
  'load number': 'loadNumber',
  'delivery date': 'deliveryDate',
  gallons: 'gallons',
  terminal: 'terminal',
  destination: 'destination',
  'pump required': 'pumpRequired'
};

const sameLoad = (a: { broker?: string; loadNumber?: string }, b: { broker?: string; loadNumber?: string }) =>
  !!a.loadNumber &&
  (a.loadNumber || '').trim() === (b.loadNumber || '').trim() &&
  (a.broker || '').trim().toLowerCase() === (b.broker || '').trim().toLowerCase();

interface DispatchImportProps {
  /** Loads already on the board, to catch duplicates */
  existingLoads: Array<{ broker?: string; loadNumber?: string }>;
  onNotify: (message: string, severity: 'success' | 'error' | 'info') => void;
}

/**
 * Drop broker dispatch PDFs here; each is read in the browser, shown for a
 * quick check, then added to the board the same way a manual load is.
 */
const DispatchImport: React.FC<DispatchImportProps> = ({ existingLoads, onNotify }) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { items: brokers = [] } = useAppSelector(state => state.brokers);
  const brokerNames: string[] = useMemo(
    () => brokers.map((broker: any) => broker.shortname).filter(Boolean),
    [brokers]
  );

  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [reading, setReading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // On the board already, or the same dispatch dropped twice in this batch.
  const isDuplicate = (row: ReviewRow) => {
    if (!row.load) return false;
    if (existingLoads.some(load => sameLoad(load, row.load!))) return true;
    const index = rows.indexOf(row);
    return rows.slice(0, index).some(other => other.load && sameLoad(other.load, row.load!));
  };

  const missingFields = (row: ReviewRow) =>
    row.load ? REQUIRED.filter(field => !String(row.load![field] ?? '').trim()) : REQUIRED;

  const onDrop = async (files: File[]) => {
    if (!files.length) return;
    setReading(true);
    setOpen(true);
    const results = await Promise.all(files.map(readDispatchFile));
    const next: ReviewRow[] = results.map((result, index) => {
      const key = `${Date.now()}-${index}-${result.file.name}`;
      if (!result.imported) {
        return { key, file: result.file, error: result.error, reviewReasons: [], include: false };
      }
      const load = { ...result.imported.load };
      // Use the broker's exact shortname from the brokers list.
      const match = brokerNames.find(name => name.toLowerCase() === load.broker.toLowerCase());
      if (match) load.broker = match;
      return {
        key,
        file: result.file,
        load,
        details: result.imported.details,
        split: result.imported.split,
        reviewReasons: result.imported.reviewReasons,
        include: true
      };
    });
    // Already on the board (or dropped twice): show it, but leave it unticked.
    next.forEach((row, index) => {
      if (!row.load) return;
      const dupe =
        existingLoads.some(load => sameLoad(load, row.load!)) ||
        next.slice(0, index).some(other => other.load && sameLoad(other.load, row.load!));
      if (dupe) row.include = false;
    });
    setRows(next);
    setExpanded(next.length === 1 ? next[0].key : null);
    setReading(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    disabled: reading || saving
  });

  const updateRow = (key: string, patch: Partial<ReviewRow>) =>
    setRows(prev => prev.map(row => (row.key === key ? { ...row, ...patch } : row)));

  const updateLoad = (key: string, patch: Partial<LoadFields>) =>
    setRows(prev =>
      prev.map(row => (row.key === key && row.load ? { ...row, load: { ...row.load, ...patch } } : row))
    );

  const toSave = rows.filter(row => row.include && row.load && !row.saved);

  // Split runs in this batch, and which of their drops are here.
  const splitGroups = useMemo(() => {
    const groups = new Map<string, { split: SplitInfo; positions: Set<number> }>();
    rows.forEach(row => {
      if (!row.split) return;
      const group = groups.get(row.split.groupId) || { split: row.split, positions: new Set<number>() };
      if (row.split.position) group.positions.add(row.split.position);
      groups.set(row.split.groupId, group);
    });
    return Array.from(groups.values());
  }, [rows]);
  const blocked = toSave.filter(row => missingFields(row).length > 0);

  // Only the load details are saved; the PDF is read here and never uploaded.
  const handleAdd = async () => {
    setSaving(true);
    let added = 0;
    for (const row of toSave) {
      updateRow(row.key, { saving: true, saveError: undefined });
      try {
        const loadData = {
          ...row.load!,
          pdfFile: null,
          pdfFileName: '',
          importedFromPdf: true,
          dispatchDetails: row.details,
          // Ties the drops of one split run together on the board.
          ...(row.split ? { split: row.split } : {})
        } as unknown as LoadFormData;
        await dispatch(uploadLoad({ loadData })).unwrap();
        updateRow(row.key, { saving: false, saved: true, include: false });
        added += 1;
      } catch (error: any) {
        updateRow(row.key, {
          saving: false,
          saveError: error?.message || 'Could not add this load'
        });
      }
    }
    setSaving(false);
    if (added) onNotify(`Added ${added} load${added === 1 ? '' : 's'} to the board`, 'success');
    if (added === toSave.length) {
      setOpen(false);
      setRows([]);
    }
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
    setRows([]);
  };

  const statusChip = (row: ReviewRow) => {
    if (row.saved) return <Chip size="small" label="Added" color="success" />;
    if (row.error) return <Chip size="small" label="Not recognized" color="error" variant="outlined" />;
    if (isDuplicate(row)) return <Chip size="small" label="Already on board" variant="outlined" />;
    const missing = missingFields(row).length;
    const unsure = row.reviewReasons.length;
    if (missing || unsure) {
      return <Chip size="small" label={`Check ${Math.max(missing, unsure)} field${Math.max(missing, unsure) === 1 ? '' : 's'}`} color="warning" />;
    }
    return <Chip size="small" label="Ready" color="success" variant="outlined" />;
  };

  const fieldError = (row: ReviewRow, field: keyof LoadFields) =>
    (REQUIRED.includes(field) && !String(row.load?.[field] ?? '').trim()) ||
    row.reviewReasons.some(reason => REASON_FIELD[reason] === field);

  return (
    <>
      <Paper
        {...getRootProps()}
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: 'pointer',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: isDragActive ? BRAND.blue : BRAND.border,
          backgroundColor: isDragActive ? '#EFF4FE' : 'background.paper',
          transition: 'background-color 150ms, border-color 150ms',
          '&:hover': { borderColor: BRAND.blue }
        }}
      >
        <input {...getInputProps()} />
        {reading ? (
          <CircularProgress size={32} />
        ) : (
          <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: BRAND.blue, flexShrink: 0 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1">
            {isDragActive ? 'Drop to read the dispatches' : 'Import dispatch PDFs'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag Titan or Avfuel dispatch requests here, or tap to choose. They're read on this
            device and only the load details are saved; you'll check them before they're added.
          </Typography>
        </Box>
      </Paper>

      <Dialog open={open} onClose={close} fullScreen={fullScreen} fullWidth maxWidth="md">
        <DialogTitle>Review imported dispatches</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
          {reading ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6, gap: 1.5 }}>
              <CircularProgress />
              <Typography color="text.secondary">Reading PDFs…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {rows.map(row => {
                const load = row.load;
                const dupe = isDuplicate(row);
                const isOpen = expanded === row.key;
                return (
                  <Paper key={row.key} variant="outlined" sx={{ p: 1.5, opacity: row.saved ? 0.7 : 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={row.include}
                        disabled={!load || row.saved || saving}
                        onChange={e => updateRow(row.key, { include: e.target.checked })}
                        inputProps={{ 'aria-label': `Add ${row.file.name}` }}
                        sx={{ ml: -1 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Typography variant="subtitle2" noWrap>
                            {load
                              ? `${load.broker} #${load.loadNumber || '?'} → ${load.destination || '?'}`
                              : row.file.name}
                          </Typography>
                          {row.split && (
                            <Chip
                              size="small"
                              label={splitLabel(row.split)}
                              sx={{ flexShrink: 0, height: 20, fontWeight: 700, backgroundColor: '#EDE9FE', color: '#5B21B6' }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {load
                            ? `${load.deliveryDate || 'No date'} · ${load.gallons || '?'} gal · ${load.terminal || 'No terminal'}${load.pumpRequired ? ' · Pump' : ''}`
                            : row.error}
                        </Typography>
                      </Box>
                      {row.saving ? <CircularProgress size={20} /> : statusChip(row)}
                      {load && (
                        <IconButton
                          size="small"
                          onClick={() => setExpanded(isOpen ? null : row.key)}
                          aria-label={isOpen ? 'Hide details' : 'Edit details'}
                        >
                          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </Box>

                    {dupe && !row.saved && (
                      <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                        Load {load?.loadNumber} is already on the board. Tick it only if you want a
                        second copy.
                      </Alert>
                    )}
                    {row.saveError && (
                      <Alert severity="error" sx={{ mt: 1, py: 0 }}>
                        {row.saveError}
                      </Alert>
                    )}

                    {load && (
                      <Collapse in={isOpen} unmountOnExit>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                            gap: 1.5,
                            mt: 1.5
                          }}
                        >
                          <FormControl size="small" error={fieldError(row, 'broker')}>
                            <InputLabel>Broker</InputLabel>
                            <Select
                              label="Broker"
                              value={load.broker}
                              onChange={e => updateLoad(row.key, { broker: e.target.value })}
                            >
                              {Array.from(new Set([load.broker, ...brokerNames])).filter(Boolean).map(name => (
                                <MenuItem key={name} value={name}>
                                  {name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Load number"
                            value={load.loadNumber}
                            error={fieldError(row, 'loadNumber')}
                            onChange={e => updateLoad(row.key, { loadNumber: e.target.value })}
                          />
                          <TextField
                            size="small"
                            label="Order number"
                            value={load.orderNumber}
                            onChange={e => updateLoad(row.key, { orderNumber: e.target.value })}
                          />
                          <TextField
                            size="small"
                            type="date"
                            label="Delivery date"
                            InputLabelProps={{ shrink: true }}
                            value={load.deliveryDate}
                            error={fieldError(row, 'deliveryDate')}
                            onChange={e => updateLoad(row.key, { deliveryDate: e.target.value })}
                          />
                          <FormControl size="small" error={fieldError(row, 'terminal')}>
                            <InputLabel>Terminal</InputLabel>
                            <Select
                              label="Terminal"
                              value={load.terminal}
                              onChange={e => updateLoad(row.key, { terminal: e.target.value })}
                            >
                              {TERMINALS.map(terminal => (
                                <MenuItem key={terminal} value={terminal}>
                                  {terminal}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Gallons"
                            type="number"
                            inputProps={{ inputMode: 'decimal' }}
                            value={load.gallons}
                            error={fieldError(row, 'gallons')}
                            onChange={e => updateLoad(row.key, { gallons: e.target.value })}
                          />
                          <TextField
                            size="small"
                            label="Destination"
                            value={load.destination}
                            error={fieldError(row, 'destination')}
                            onChange={e => updateLoad(row.key, { destination: e.target.value })}
                            sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}
                          />
                          <FormControl size="small" error={fieldError(row, 'driver')}>
                            <InputLabel>Driver</InputLabel>
                            <Select
                              label="Driver"
                              value={load.driver}
                              onChange={e => updateLoad(row.key, { driver: e.target.value })}
                            >
                              {DRIVERS.map(driver => (
                                <MenuItem key={driver} value={driver}>
                                  {driver}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={load.pumpRequired}
                                onChange={e => updateLoad(row.key, { pumpRequired: e.target.checked })}
                              />
                            }
                            label={
                              <Typography
                                variant="body2"
                                color={fieldError(row, 'pumpRequired') ? 'error' : undefined}
                              >
                                Pump required
                              </Typography>
                            }
                          />
                        </Box>

                        {row.details && (
                          <Box sx={{ mt: 1.5, display: 'grid', gap: 0.5 }}>
                            {[
                              ['Window', row.details.deliveryWindow],
                              ['Ship to', [row.details.destinationName, row.details.destinationAddress].filter(Boolean).join(' · ')],
                              ['Contact', row.details.destinationPhone],
                              ['Pickup', [row.details.supplier, row.details.terminalName].filter(Boolean).join(' · ')],
                              ['Instructions', row.details.instructions],
                              ['Comments', row.details.comments]
                            ]
                              .filter(([, value]) => value)
                              .map(([label, value]) => (
                                <Typography key={label} variant="caption" color="text.secondary">
                                  <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {label}:
                                  </Box>{' '}
                                  {value}
                                </Typography>
                              ))}
                            {row.details.newDestination && (
                              <Typography variant="caption" sx={{ color: BRAND.pending.text }}>
                                New delivery point: not on your customer list yet, so the name is taken
                                from the PDF.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Collapse>
                    )}
                  </Paper>
                );
              })}
              {splitGroups.map(({ split, positions }) => {
                const missing = split.stops.filter((_, index) => !positions.has(index + 1));
                return (
                  <Alert key={split.groupId} severity={missing.length ? 'warning' : 'info'}>
                    <strong>{split.ways}-way split</strong> · {split.totalGallons.toLocaleString('en-US')} gal ·{' '}
                    {describeStops(split.stops)}
                    {missing.length > 0 && (
                      <>
                        <br />
                        Missing {missing.length === 1 ? 'drop' : 'drops'}: {describeStops(missing)}. Add{' '}
                        {missing.length === 1 ? 'it' : 'them'} too, or add these now and the rest later.
                      </>
                    )}
                  </Alert>
                );
              })}
              {blocked.length > 0 && (
                <Alert severity="warning">
                  Fill in the highlighted fields on {blocked.length} load
                  {blocked.length === 1 ? '' : 's'} before adding.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={close} disabled={saving} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={reading || saving || toSave.length === 0 || blocked.length > 0}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saving
              ? 'Adding…'
              : `Add ${toSave.length} load${toSave.length === 1 ? '' : 's'} to board`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DispatchImport;
